import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send, Sparkles, Wrench, ChevronDown, ChevronRight } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

type Msg = { role: "user" | "assistant"; content: string; tool_events?: any[] };

type Props = {
  endpoint: "statement-chat" | "statement-admin-ai";
  title?: string;
  placeholder?: string;
  suggestions?: string[];
  extraBody?: Record<string, unknown>;
  onAfterReply?: () => void;
};

export function StatementChat({ endpoint, title, placeholder, suggestions, extraBody, onAfterReply }: Props) {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  useEffect(() => { taRef.current?.focus(); }, []);

  const send = async (text?: string) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    const next: Msg[] = [...messages, { role: "user", content }];
    setMessages(next);
    setInput("");
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke(endpoint, {
        body: {
          messages: next.map(({ role, content }) => ({ role, content })),
          ...(extraBody ?? {}),
        },
      });
      if (error) throw error;
      const reply = (data as any)?.reply ?? "";
      const tool_events = (data as any)?.tool_events;
      setMessages((m) => [...m, { role: "assistant", content: reply || "(no response)", tool_events }]);
      onAfterReply?.();
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `Error: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
      setTimeout(() => taRef.current?.focus(), 0);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Sparkles className="h-4 w-4 text-primary" />
          {title ?? "Ask AI"}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div ref={scrollRef} className="max-h-[400px] min-h-[200px] overflow-y-auto space-y-4 pr-1">
          {messages.length === 0 && (
            <div className="text-sm text-muted-foreground">
              {placeholder ?? "Ask anything about the statement."}
              {suggestions && suggestions.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {suggestions.map((s) => (
                    <Button key={s} variant="outline" size="sm" onClick={() => send(s)}>
                      {s}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className="space-y-2">
              {m.role === "user" ? (
                <div className="flex justify-end">
                  <div className="bg-primary text-primary-foreground rounded-lg px-3 py-2 text-sm max-w-[85%] whitespace-pre-wrap">
                    {m.content}
                  </div>
                </div>
              ) : (
                <div className="text-sm prose prose-sm dark:prose-invert max-w-none prose-p:my-2 prose-table:my-2 prose-headings:mt-3">
                  <ReactMarkdown>{m.content}</ReactMarkdown>
                  {m.tool_events && m.tool_events.length > 0 && (
                    <Collapsible>
                      <CollapsibleTrigger className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mt-2">
                        <Wrench className="h-3 w-3" />
                        <span>{m.tool_events.length} action{m.tool_events.length === 1 ? "" : "s"}</span>
                        <ChevronDown className="h-3 w-3" />
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="mt-2 space-y-1">
                          {m.tool_events.map((e: any, idx: number) => (
                            <div key={idx} className="text-xs bg-muted/50 rounded p-2 font-mono overflow-x-auto">
                              <div className="font-semibold">{e.tool}</div>
                              <div className="text-muted-foreground">args: {JSON.stringify(e.args)}</div>
                              <div className="text-muted-foreground">result: {JSON.stringify(e.result).slice(0, 300)}</div>
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </div>
              )}
            </div>
          ))}
          {busy && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Thinking…
            </div>
          )}
        </div>

        <div className="flex gap-2 items-end">
          <Textarea
            ref={taRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
            }}
            placeholder="Ask a question…"
            rows={2}
            className="resize-none"
            disabled={busy}
          />
          <Button onClick={() => send()} disabled={busy || !input.trim()} size="icon" className="h-10 w-10 shrink-0">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
